import {
  useCreateMenu,
  useDeleteMenu,
  useMenuActivations,
  useMenus,
  useSetMenuActivation,
  useUpdateMenu,
} from '@/features/menu/hooks/useMenu';
import EmptyState from '@/components/EmptyState';
import { getT, useT } from '@/i18n';
import { useSettingsDetailBackLink } from '@/utils/useMediaQuery';
import { sectionRouter } from '@/utils/navigateMain';
import { AiSparkle } from '@/components/AiSurface';
import { useCanUseAi } from '@/features/ai-assistant/hooks/useCanUseAi';
import { compactLocalized, loc } from '@/utils/localized';
import type { Menu, MenuChannel } from '@restaurant/api-contracts';
import { useEffect, useRef, useState } from 'react';
import {
  Block,
  BlockTitle,
  Button,
  Chip,
  Link as F7Link,
  List,
  ListInput,
  ListItem,
  Navbar,
  NavLeft,
  NavTitle,
  NavRight,
  Page,
  PageContent,
  Popover,
  Popup,
  Preloader,
  f7,
} from 'framework7-react';
import { ToggleSwitch } from '@/components/ToggleSwitch';

function fail(e: unknown) {
  f7.dialog.alert(e instanceof Error ? e.message : getT().error);
}

function SectionHelp({ children }: { children: React.ReactNode }) {
  return (
    <Block style={{ marginTop: 0, marginBottom: 0 }}>
      <p style={{ color: 'var(--f7-block-footer-text-color)', fontSize: 13 }}>{children}</p>
    </Block>
  );
}

// Order channels customers can pick. DINE_IN powers in-house QR/table ordering;
// PICKUP/DELIVERY power the public storefront.
const CHANNEL_OPTIONS: { value: MenuChannel }[] = [
  { value: 'DINE_IN' },
  { value: 'PICKUP' },
  { value: 'DELIVERY' },
];
function channelLabel(c: MenuChannel, t: ReturnType<typeof useT>): string {
  switch (c) {
    case 'PICKUP':
      return t.menu.productForm.pickup;
    case 'DELIVERY':
      return t.menu.productForm.delivery;
    default:
      return t.menu.productForm.dineIn;
  }
}

// Leading icon per channel - gives each "Live menus" row a left anchor the
// picker popover opens from (so it pops on the left, not centred), and matches
// the order inspector's pickup/delivery glyphs.
const CHANNEL_ICON: Record<MenuChannel, string> = {
  DINE_IN: 'house_fill',
  PICKUP: 'bag_fill',
  DELIVERY: 'car_fill',
};

// ─── Menu sheet (create / edit a menu) ───────────────────────────────────────
// Follows Rule 10: form state lives in MenuSheetContent (a child of <PushSheet>)
// and is synced from props on open. Closing flows through the `open` prop.

function MenuSheet({
  open,
  onClose,
  menu,
}: {
  open: boolean;
  onClose: () => void;
  menu: Menu | null;
}) {
  return (
    <Popup opened={open} onPopupClosed={onClose}>
      <Page pageContent={false}>
        <MenuSheetContent open={open} menu={menu} onClose={onClose} />
      </Page>
    </Popup>
  );
}

function MenuSheetContent({
  open,
  menu,
  onClose,
}: {
  open: boolean;
  menu: Menu | null;
  onClose: () => void;
}) {
  const t = useT();
  const createMenu = useCreateMenu();
  const updateMenu = useUpdateMenu();
  const editing = !!menu;

  const [name, setName] = useState<Record<string, string>>({});

  // Sync from props ON OPEN (Rule 10).
  useEffect(() => {
    if (!open) return;
    setName(menu ? { ...(menu.name as Record<string, string>) } : {});
  }, [open, menu]);

  const canSave = !!name.en?.trim();

  const submit = () => {
    if (!canSave) return;
    const namePayload = compactLocalized(name)!;
    if (editing && menu) {
      updateMenu.mutate(
        { menuId: menu.id, body: { name: namePayload } },
        { onSuccess: onClose, onError: fail },
      );
    } else {
      createMenu.mutate({ name: namePayload }, { onSuccess: onClose, onError: fail });
    }
  };

  return (
    <>
      {/* Cancel on the left keeps the title centered - same as the add-product
          popup. */}
      <Navbar>
        <NavLeft>
          <F7Link popupClose>{t.cancel}</F7Link>
        </NavLeft>
        <NavTitle>{editing ? t.menu.editMenu : t.menu.newMenu}</NavTitle>
        <NavRight>
          <F7Link
            onClick={submit}
            style={canSave ? undefined : { opacity: 0.4, pointerEvents: 'none' }}
          >
            {t.saveShort}
          </F7Link>
        </NavRight>
      </Navbar>
      <PageContent>
        <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
          <ListInput
            label={t.menu.productForm.name}
            type="text"
            required
            validate
            value={name.en ?? ''}
            onInput={(e) => setName({ ...name, en: (e.target as HTMLInputElement).value })}
            errorMessage={t.menu.nameRequired}
            errorMessageForce={!name.en?.trim()}
          />
        </List>
      </PageContent>
    </>
  );
}

// ─── Live menus panel (which menu each order channel shows) ──────────────────
// Two modes behind one switch. Simple (default): pick one menu that serves every
// channel. Advanced: assign a menu per channel. The API is per-channel either
// way - simple mode just writes the same menu to all three. "None" clears a
// channel (customers see an empty state there).

// `ALL` is the simple-mode target: choosing a menu writes it to every channel.
type PickerTarget = 'ALL' | MenuChannel;

function LiveMenusPanel({ menus }: { menus: Menu[] }) {
  const t = useT();
  const { data: activations } = useMenuActivations();
  const setActivation = useSetMenuActivation();
  // Single popover instance reused across rows; `pickerTarget` says which row's
  // value is being edited. Same control as the reservation status picker
  // (ReservationActions): tap a row → anchored bubble of selectable rows.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const popoverRef = useRef<any>(null);
  // Per-row left-icon anchors: the popover opens from the row's leading icon so
  // it pops up on the left, instead of centred under the full-width row.
  const anchorRefs = useRef<Record<string, HTMLElement | null>>({});
  const [pickerTarget, setPickerTarget] = useState<PickerTarget | null>(null);

  const liveMenuFor = (channel: MenuChannel): string =>
    activations?.find((a) => a.channel === channel)?.menuId ?? '';

  const channelIds = CHANNEL_OPTIONS.map(({ value }) => liveMenuFor(value));
  const allSame = channelIds.every((id) => id === channelIds[0]);
  // In simple mode the single row reflects the shared menu when channels agree.
  // When they differ it's "Mixed": we neither overwrite nor pretend a single
  // menu is set - the operator picks one, which then unifies every channel.
  const MIXED = '__mixed__';
  const simpleId = allSame ? channelIds[0] : MIXED;

  // Seed the mode from saved state once activations load: channels that already
  // differ open in advanced; everything else defaults to simple.
  const [perChannel, setPerChannel] = useState(false);
  const seeded = useRef(false);
  useEffect(() => {
    if (!activations || seeded.current) return;
    seeded.current = true;
    setPerChannel(!allSame);
  }, [activations, allSame]);

  const NONE_LABEL = t.menu.noneHidden;
  const menuLabel = (menuId: string): string => {
    const m = menus.find((x) => x.id === menuId);
    return m ? loc(m.name) : NONE_LABEL;
  };

  const openPicker = (target: PickerTarget) => {
    setPickerTarget(target);
    requestAnimationFrame(() => {
      const anchor = anchorRefs.current[target];
      if (popoverRef.current?.el && anchor) f7.popover.open(popoverRef.current.el, anchor);
    });
  };

  const choose = (menuId: string | null) => {
    if (pickerTarget === 'ALL') {
      for (const { value } of CHANNEL_OPTIONS)
        setActivation.mutate({ channel: value, menuId }, { onError: fail });
    } else if (pickerTarget) {
      setActivation.mutate({ channel: pickerTarget, menuId }, { onError: fail });
    }
    f7.popover.close();
  };

  // Highlight the live option in the popover. In simple/Mixed state simpleId is
  // MIXED, which matches no option, so nothing is checked - a deliberate blank.
  const activeId =
    pickerTarget === 'ALL' ? simpleId : pickerTarget ? liveMenuFor(pickerTarget) : '';

  // A selectable row for the popover - title + a trailing checkmark (right) on
  // the currently-live option, the iOS list-select idiom.
  const option = (key: string, label: string, selected: boolean, onClick: () => void) => (
    <ListItem
      key={key}
      title={label}
      link="#"
      onClick={(e: React.MouseEvent) => {
        e.preventDefault();
        onClick();
      }}
    >
      {selected && (
        <i
          slot="after"
          className="f7-icons"
          style={{ color: 'var(--f7-theme-color)', fontSize: 20 }}
        >
          checkmark_alt
        </i>
      )}
    </ListItem>
  );

  // A menu-picking row: leading channel/menu icon (the popover anchor), title,
  // and the current selection on the right.
  const pickerRow = (target: PickerTarget, icon: string, title: string, after: string) => (
    <ListItem
      key={target}
      title={title}
      link="#"
      after={after}
      disabled={setActivation.isPending}
      onClick={(e: React.MouseEvent) => {
        e.preventDefault();
        openPicker(target);
      }}
    >
      <i
        slot="media"
        ref={(el) => {
          anchorRefs.current[target] = el;
        }}
        className="f7-icons"
        style={{ fontSize: 22, color: 'var(--f7-theme-color)' }}
      >
        {icon}
      </i>
    </ListItem>
  );

  return (
    <>
      <BlockTitle>{perChannel ? t.menu.liveMenus : t.menu.liveMenu}</BlockTitle>
      <SectionHelp>
        {perChannel ? t.menu.assignMenuPerChannelHelp : t.menu.liveMenuHelp}
      </SectionHelp>
      <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
        <ListItem title={t.menu.differentMenuPerChannel}>
          <ToggleSwitch slot="after" checked={perChannel} onChange={setPerChannel} />
        </ListItem>
        {perChannel
          ? CHANNEL_OPTIONS.map(({ value: channel }) => {
              const id = liveMenuFor(channel);
              return pickerRow(
                channel,
                CHANNEL_ICON[channel],
                channelLabel(channel, t),
                id ? menuLabel(id) : NONE_LABEL,
              );
            })
          : pickerRow(
              'ALL',
              'square_list',
              t.menu.menuFieldLabel,
              simpleId === MIXED ? t.menu.mixed : simpleId ? menuLabel(simpleId) : NONE_LABEL,
            )}
      </List>

      <Popover ref={popoverRef} style={{ minWidth: 220 }}>
        <List noChevron style={{ minWidth: 220, margin: 0 }}>
          {option('__none__', NONE_LABEL, activeId === '', () => choose(null))}
          {menus.map((m) => option(m.id, loc(m.name), activeId === m.id, () => choose(m.id)))}
        </List>
      </Popover>
    </>
  );
}

function MenusPage() {
  const t = useT();
  // Reached from Settings via navigateMain (pushed onto the settings tab stack on
  // mobile), so a mobile back button returns to Settings. Desktop swaps the main
  // view root → undefined, no dead chevron. F7 auto-hides it when Menu is a view
  // root (the Menu tab itself), so it only shows when there's somewhere to go.
  const settingsBackLink = useSettingsDetailBackLink();
  const { data: menus, isLoading } = useMenus();
  const deleteMenu = useDeleteMenu();
  const canUseAi = useCanUseAi();

  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingMenu, setEditingMenu] = useState<Menu | null>(null);

  const openCreate = () => {
    setEditingMenu(null);
    setSheetOpen(true);
  };
  const openCreateWithAi = () => sectionRouter()?.navigate('/menu/create-with-ai');
  const pickAdd = (action: () => void) => (e: React.MouseEvent) => {
    e.preventDefault();
    f7.popover.close('.menus-add-popover');
    action();
  };
  const openEdit = (m: Menu) => {
    setEditingMenu(m);
    setSheetOpen(true);
  };

  const removeMenu = (m: Menu) => {
    if (m.liveChannels.length > 0) {
      f7.toast.show({ text: t.menu.cantDeleteActiveMenu, closeTimeout: 3000 });
      return;
    }
    f7.dialog.confirm(t.menu.deleteMenuConfirm(loc(m.name)), t.menu.deleteMenu, () => {
      deleteMenu.mutate({ menuId: m.id }, { onError: fail });
    });
  };

  return (
    <Page name="menus">
      <Navbar title={t.menu.title} backLink={settingsBackLink}>
        <NavRight>
          <F7Link
            iconF7="plus"
            aria-label={t.menu.newMenu}
            onClick={(e: React.MouseEvent) => {
              if (!canUseAi) {
                openCreate();
                return;
              }
              if (e.currentTarget instanceof HTMLElement) {
                f7.popover.open('.menus-add-popover', e.currentTarget);
              }
            }}
            data-testid="menus-add"
          />
        </NavRight>
      </Navbar>

      {isLoading && (
        <Block className="text-align-center">
          <Preloader />
        </Block>
      )}

      {!isLoading && (!menus || menus.length === 0) && (
        <Block>
          <EmptyState
            icon="square_list"
            title={t.menu.noMenusYet}
            description={t.menu.createMenuDescription}
          />
          {canUseAi && (
            <Button
              fill
              round
              large
              onClick={openCreateWithAi}
              style={{ marginTop: 16 }}
              data-testid="menus-empty-create-ai"
            >
              <AiSparkle size={18} />
              <span>{t.menuCreate.title}</span>
            </Button>
          )}
          <Button
            fill={!canUseAi}
            round
            large
            onClick={openCreate}
            style={{ marginTop: canUseAi ? 8 : 16 }}
            data-testid="menus-empty-create"
          >
            {t.menu.createMenu}
          </Button>
        </Block>
      )}

      {!isLoading && menus && menus.length > 0 && (
        <>
          <LiveMenusPanel menus={menus} />

          <BlockTitle>{t.menu.allMenus}</BlockTitle>
          <Block style={{ marginTop: 0, marginBottom: 0 }}>
            <p style={{ color: 'var(--f7-block-footer-text-color)', fontSize: 14 }}>
              {t.menu.allMenusHelp}
            </p>
          </Block>
          <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
            {menus.map((m) => (
              <ListItem key={m.id} link={`/menu/${m.id}`} swipeout title={loc(m.name)}>
                {m.liveChannels.length > 0 && (
                  <Chip
                    slot="after"
                    color="green"
                    text={m.liveChannels.map((c) => channelLabel(c, t)).join(', ')}
                  />
                )}
                <div slot="swipeout-actions-right">
                  <button type="button" className="swipeout-close" onClick={() => openEdit(m)}>
                    {t.edit}
                  </button>
                  <button
                    type="button"
                    className="swipeout-close color-red"
                    onClick={() => removeMenu(m)}
                  >
                    {t.delete}
                  </button>
                </div>
              </ListItem>
            ))}
          </List>

          <BlockTitle>{t.menu.allergenLabeling.sectionTitle}</BlockTitle>
          <SectionHelp>{t.menu.allergenLabeling.sectionHelp}</SectionHelp>
          <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
            <ListItem
              link="/menu/allergen-labeling"
              title={t.menu.allergenLabeling.title}
              footer={t.menu.allergenLabeling.sectionSubtitle}
            />
          </List>
        </>
      )}

      <MenuSheet open={sheetOpen} onClose={() => setSheetOpen(false)} menu={editingMenu} />
      <Popover className="menus-add-popover" style={{ minWidth: 220 }}>
        <List noChevron style={{ minWidth: 220, margin: 0 }}>
          <ListItem
            title={t.menu.newMenu}
            link="#"
            onClick={pickAdd(openCreate)}
            data-testid="menus-add-new"
          >
            <i
              slot="media"
              className="f7-icons"
              style={{ fontSize: 22, color: 'var(--f7-theme-color)' }}
            >
              square_list
            </i>
          </ListItem>
          {canUseAi && (
            <ListItem
              title={t.menuCreate.title}
              link="#"
              onClick={pickAdd(openCreateWithAi)}
              data-testid="menus-add-ai"
            >
              <span slot="media" className="menus-add-popover__sparkle">
                <AiSparkle size={22} />
              </span>
            </ListItem>
          )}
        </List>
      </Popover>
    </Page>
  );
}

export default MenusPage;
